import React, { useEffect, useState, useRef } from "react";
import { useWindowSize } from "../../global/hooks/viewportHooks";
import { Loader } from "@vds/loaders";
import { BreadcrumbItem, Breadcrumbs } from "@vds/breadcrumbs";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  HeaderDivUsrGrpDet,
  GrpNameAndSearchDiv,
  SiteNameInputAndButtonsDiv,
  ButtonGroupWrapper,
} from "./styles";
import SearchInput from "../../global/utility/Utils";
import { Input } from "@vds/inputs";
import { ButtonGroup } from "@vds/buttons";
import { Icon } from "@vds/icons";
import UserGroupDetailsTable from "../../global/components/Tables/UserGroupDetailsTable";
import {
  getIndividualGroupDetail,
  addUsersToExistingGroup,
  removeUsersToExistingGroup,
  updateUserGroupName,
} from "./userGroupsService";
import { setGroupDetails } from "../../store/actions";
import { renderStepThreeContent } from "./Utils";
import { Modal } from "@vds/modals";
import { Title } from "@vds/typography";
import { showNotificationBanner } from "../../store/actions";
import { getUsersFilteredBySiteAndRole } from "../../model/roleService";
import RemoveGroupModal from "../UserDetails/RemoveGroupModal";
import { USER_GROUP_DETAILS_CONSTANTS } from "../../global/constants/constants";
import "./UserGroupsModals.css";

function UserGroupsDetails() {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [userData, setUserData] = useState([]);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isEditingGroupName, setIsEditingGroupName] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [filteredUserData, setFilteredUserData] = useState([]);
  const [newSelectedMembers, setNewSelectedMembers] = useState([]);

  const [bannerData, setBannerData] = useState("");
  const [siteId, setSiteId] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const { width: windowWidth } = useWindowSize();
  const isMobileViewport = windowWidth && windowWidth < 768;

  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );
  const { groupDetails, groupName, siteName, groupMembers } = useSelector(
    (state) => state.userDetailsGroupReducer
  );
  const localCachedUsersRef = useRef([]);
  const localCachedSiteIdRef = useRef(null);

  const { response } = location.state || {};
  const userGroupID =
    response?.data?.id || groupDetails?.id || response?.id || "";

  useEffect(() => {
    if (!userGroupID) {
      console.error("User group ID is missing.");
      setTableLoading(false);
      return;
    }

    if (userGroupID && activeSession?.accountId) {
      fetchGroupDetails(userGroupID);
    }

    return () => {
      dispatch(setGroupDetails({}));
    };
  }, [userGroupID, activeSession, dispatch]);

  const fetchUsers = async () => {
    try {
      const apiResponse = await getUsersFilteredBySiteAndRole(
        activeSession,
        [],
        [siteId] || []
      );
      const users = apiResponse || [];
      localCachedUsersRef.current = users;
      localCachedSiteIdRef.current = siteId;
      setUserData(users);
      if (searchQuery?.trim()) {
        const filtered = users.filter((user) =>
          `${user?.firstname} ${user?.lastname}`
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        );
        setFilteredUserData(filtered);
      } else {
        setFilteredUserData(users);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupDetails = async (groupId) => {
    setTableLoading(true);
    try {
      const result = await getIndividualGroupDetail(groupId, activeSession);
      if (result && result.length > 0) {
        setSiteId(result[0]?.tagids?.[0]);
        dispatch(setGroupDetails(result[0]));
        setNewGroupName(result[0]?.name || "");
      }
    } catch (error) {
      console.error("Failed to fetch group details:", error);
    } finally {
      setTableLoading(false);
    }
  };

  const editGroupClickHandler = () => {
    if (isEditingGroupName) {
      handleGroupNameSave();
    } else {
      setIsEditingGroupName(true);
    }
  };

  const handleSearchChange = (e) => {
    const searchedValue = e?.target?.value;
    setSearchQuery(searchedValue);
    if (searchedValue?.trim() === "") {
      setFilteredUserData(userData);
    } else {
      const filtered = userData?.filter((user) =>
        `${user?.firstname} ${user?.lastname}`
          .toLowerCase()
          .includes(searchedValue?.toLowerCase())
      );
      setFilteredUserData(filtered);
    }
  };

  const filteredMembers = groupMembers.filter((member) =>
    `${member.firstname} ${member.lastname} ${member.email}`
      .toLowerCase()
      .includes(searchQuery)
  );

  const handleAddMembersClick = () => {
    const isSameSite = localCachedSiteIdRef.current === siteId;
    const hasCachedUsers = localCachedUsersRef.current.length > 0 && isSameSite;
    if (hasCachedUsers) {
      setUserData(localCachedUsersRef.current);
      setFilteredUserData(localCachedUsersRef.current);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setShowAddMembers(true);
    fetchUsers();
  };

  const handleCheckboxChange = (user) => {
    setSelectedUsers((prev) =>
      prev.includes(user)
        ? prev.filter((u) => u.id !== user.id)
        : [...prev, user]
    );
  };

  const handleCancel = () => {
    setShowAddMembers(false);
    setSearchQuery("");
    setFilteredUserData(userData);
  };

  const handleAddMembers = async () => {
    handleCancel();
    setIsSaving(true);
    try {
      const newUsers = selectedUsers
        .filter(
          (selectedUser) =>
            !groupMembers.some((member) => member.id === selectedUser.id)
        )
        .map((user) => user);

      if (newUsers.length > 0) {
        const response = await addUsersToExistingGroup(
          userGroupID,
          newUsers.map((user) => user.id),
          activeSession
        );
        let userNames =
          newUsers.length > 0
            ? newUsers.map((user) => `${user.firstname} ${user.lastname}`)
            : [];
        userNames = newUsers.length > 1 ? userNames.join(" , ") : userNames;
        const subTitle =
          newUsers.length > 1
            ? `${userNames} are added to the ${groupName} group`
            : `${userNames} has been added to the ${groupName} group`;
        setBannerData(userNames);
        if (response?.status === 200) {
          const notificationInfo = {
            type: USER_GROUP_DETAILS_CONSTANTS.NOTIFICATION_TYPE_USERS_ADDED,
            priority: USER_GROUP_DETAILS_CONSTANTS.PRIORITY_SUCCESS,
            data: { subTitle: subTitle },
            showNotification: true,
          };
          dispatch(showNotificationBanner(notificationInfo));
          await fetchGroupDetails(userGroupID);
        } else {
          const notificationInfo = {
            type: USER_GROUP_DETAILS_CONSTANTS.NOTIFICATION_TYPE_USERS_ADDED,
            priority: USER_GROUP_DETAILS_CONSTANTS.PRIORITY_CRITICAL,
            data: { subTitle: USER_GROUP_DETAILS_CONSTANTS.USERS_ADDED_FAILURE(userNames) },
            showNotification: true,
          };
          dispatch(showNotificationBanner(notificationInfo));
        }
      }
    } catch (error) {
      const notificationInfo = {
        type: USER_GROUP_DETAILS_CONSTANTS.NOTIFICATION_TYPE_USERS_ADDED,
        priority: USER_GROUP_DETAILS_CONSTANTS.PRIORITY_CRITICAL,
        data: { subTitle: USER_GROUP_DETAILS_CONSTANTS.USERS_ADDED_FAILURE(bannerData) },
        showNotification: true,
      };
      dispatch(showNotificationBanner(notificationInfo));
    } finally {
      setIsSaving(false);
      setNewSelectedMembers([]);
    }
  };

  const handleDeleteUserClick = (userId, name = "") => {
    setUserToDelete({ userId, name });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleConfirmDeleteUser = async () => {
    if (!userToDelete) return;
    const { userId, name } = userToDelete;
    setShowDeleteModal(false);
    setUserToDelete(null);
    setIsSaving(true);
    try {
      const response = await removeUsersToExistingGroup(
        userGroupID,
        [userId],
        activeSession
      );
      setBannerData(name);
      if (response.status === 200) {
        const notificationInfo = {
          type: USER_GROUP_DETAILS_CONSTANTS.NOTIFICATION_TYPE_USER_DELETED,
          priority: USER_GROUP_DETAILS_CONSTANTS.PRIORITY_SUCCESS,
          data: { subTitle: USER_GROUP_DETAILS_CONSTANTS.USER_DELETED_SUCCESS(name) },
          showNotification: true,
        };
        dispatch(showNotificationBanner(notificationInfo));
        await fetchGroupDetails(userGroupID);
        setSelectedUsers((prevSelectedUsers) =>
          prevSelectedUsers.filter((user) => user.id !== userId)
        );
      } else {
        const notificationInfo = {
          type: USER_GROUP_DETAILS_CONSTANTS.NOTIFICATION_TYPE_USER_DELETED,
          priority: USER_GROUP_DETAILS_CONSTANTS.PRIORITY_CRITICAL,
          data: { subTitle: USER_GROUP_DETAILS_CONSTANTS.USER_DELETED_FAILURE(name) },
          showNotification: true,
        };
        dispatch(showNotificationBanner(notificationInfo));
      }
    } catch (error) {
      const notificationInfo = {
        type: USER_GROUP_DETAILS_CONSTANTS.NOTIFICATION_TYPE_USER_DELETED,
        priority: USER_GROUP_DETAILS_CONSTANTS.PRIORITY_CRITICAL,
        data: { subTitle: USER_GROUP_DETAILS_CONSTANTS.USER_REMOVED_FAILURE(name) },
        showNotification: true,
      };
      dispatch(showNotificationBanner(notificationInfo));
    } finally {
      setIsSaving(false);
    }
  };

  const handleGroupNameChange = (e) => {
    setNewGroupName(e.target.value);
  };

  const handleGroupNameSave = async () => {
    if (newGroupName !== groupName) {
      try {
        const updatedGroupDetails = await updateUserGroupName(
          userGroupID,
          newGroupName,
          activeSession
        );
        if (updatedGroupDetails?.status === 200) {
          const notificationInfo = {
            type: USER_GROUP_DETAILS_CONSTANTS.NOTIFICATION_TYPE_GROUP_RENAMED,
            priority: USER_GROUP_DETAILS_CONSTANTS.PRIORITY_SUCCESS,
            data: {
              subTitle: USER_GROUP_DETAILS_CONSTANTS.GROUP_RENAMED_SUCCESS(groupName, newGroupName),
            },
            showNotification: true,
          };
          dispatch(showNotificationBanner(notificationInfo));
          await fetchGroupDetails(userGroupID);
        } else {
          const notificationInfo = {
            type: USER_GROUP_DETAILS_CONSTANTS.NOTIFICATION_TYPE_GROUP_RENAMED,
            priority: USER_GROUP_DETAILS_CONSTANTS.PRIORITY_CRITICAL,
            data: {
              subTitle: USER_GROUP_DETAILS_CONSTANTS.GROUP_RENAMED_FAILURE(groupName, newGroupName),
            },
            showNotification: true,
          };
          dispatch(showNotificationBanner(notificationInfo));
        }
        dispatch(
          setGroupDetails({
            ...groupDetails,
            name: newGroupName,
          })
        );
      } catch (error) {
        const isSystemGroup =
          error?.response?.data?.original_error_description
            ?.toLowerCase()
            .includes("system group");
        const subTitle = isSystemGroup
          ? USER_GROUP_DETAILS_CONSTANTS.GROUP_SYSTEM_RENAME_ERROR
          : USER_GROUP_DETAILS_CONSTANTS.GROUP_RENAMED_ERROR(newGroupName);
        const notificationInfo = {
          type: USER_GROUP_DETAILS_CONSTANTS.NOTIFICATION_TYPE_GROUP_RENAMED,
          priority: USER_GROUP_DETAILS_CONSTANTS.PRIORITY_CRITICAL,
          data: { subTitle },
          showNotification: true,
        };
        dispatch(showNotificationBanner(notificationInfo));
      }
    }
    setIsEditingGroupName(false);
  };

  const handleGroupNameBlur = () => {
    handleGroupNameSave();
  };

  const handleUserDetailsClick = (user) => {
    navigate('/userdetails', {
      state: {
        ...user,
        tabName: 'Users',
        fromPage: USER_GROUP_DETAILS_CONSTANTS.FROM_PAGE,
      }
    });
  };

  return (
    <div className="user-groups-page">
      <Loader active={isSaving} fullscreen />
      <HeaderDivUsrGrpDet>
        <Breadcrumbs surface="light">
          <BreadcrumbItem>
            <Link
              data-testid={USER_GROUP_DETAILS_CONSTANTS.TESTID_SETTINGS_LINK}
              to="/settings"
              className="breadcrums-item_link"
            >
              {USER_GROUP_DETAILS_CONSTANTS.SETTINGS_LINK_TEXT}
            </Link>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <Link
              data-testid={USER_GROUP_DETAILS_CONSTANTS.TESTID_USER_GROUPS_LINK}
              to="/usergroups"
              state={{ tabName: "User Groups" }}
              className="breadcrums-item_link"
            >
              {USER_GROUP_DETAILS_CONSTANTS.USER_GROUPS_LINK_TEXT}
            </Link>
          </BreadcrumbItem>
          <BreadcrumbItem>{groupName}</BreadcrumbItem>
        </Breadcrumbs>
        <GrpNameAndSearchDiv>
          {isEditingGroupName ? (
            <Input
              type="text"
              value={newGroupName}
              onChange={handleGroupNameChange}
              autoFocus
            />
          ) : (
            groupName ? <Title size="large" bold={false}>{groupName}</Title> : null
          )}
        </GrpNameAndSearchDiv>
      </HeaderDivUsrGrpDet>
      <SiteNameInputAndButtonsDiv>
        <Title size="small">
          <b>{USER_GROUP_DETAILS_CONSTANTS.LABEL_SITE}:</b><span className="user-groups-site-value">{siteName}</span>
        </Title>

        <ButtonGroupWrapper>
          <ButtonGroup
            rowQuantity={{ mobile: 1, tablet: 2, desktop: 2 }}
            data={[
              {
                children: USER_GROUP_DETAILS_CONSTANTS.ADD_MEMBERS,
                size: "medium",
                use: "primary",
                width: isMobileViewport ? "100%" : "auto",
                "data-testid": USER_GROUP_DETAILS_CONSTANTS.TESTID_ADD_MEMBERS_BUTTON,
                onClick: handleAddMembersClick,
              },
              {
                children: isEditingGroupName
                  ? USER_GROUP_DETAILS_CONSTANTS.SAVE_NAME
                  : USER_GROUP_DETAILS_CONSTANTS.RENAME_GROUP,
                size: "medium",
                use: "secondary",
                width: isMobileViewport ? "100%" : "auto",
                "data-testid": USER_GROUP_DETAILS_CONSTANTS.TESTID_SAVE_RENAME_BUTTON,
                onClick: editGroupClickHandler,
              },
            ]}
          />
        </ButtonGroupWrapper>
      </SiteNameInputAndButtonsDiv>
      <div className="user-groups-search-wrapper">
        <SearchInput
          placeholder={USER_GROUP_DETAILS_CONSTANTS.SEARCH_MEMBERS_PLACEHOLDER}
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </div>
      <UserGroupDetailsTable
        groupMembers={filteredMembers}
        loading={tableLoading}
        onDelete={handleDeleteUserClick}
        onUserDetailsClick={handleUserDetailsClick}
      />

      <RemoveGroupModal
        opened={showDeleteModal}
        groupToRemove={
          userToDelete
            ? { userName: userToDelete.name, groupName: groupName }
            : null
        }
        onConfirm={handleConfirmDeleteUser}
        onClose={handleCloseDeleteModal}
      />

      <Modal
        width="60vw"
        opened={showAddMembers}
        disableAnimation={false}
        disableOutsideClick={true}
        className="user-groups-modal"
        hideCloseButton={true}
        closeButton={
          <div
            className="modal-close-button"
            data-testid="userGroupDetails_addMembersCloseButton"
            onClick={handleCancel}
          >
            <Icon name="close" ariaHidden={true} />
          </div>
        }
        onOpenedChange={(opened) => {
          if (!opened) {
            handleCancel();
          }
        }}
        ariaLabel="Add Members"
      >
        {renderStepThreeContent({
              userData: filteredUserData,
              selectedMembers: filteredMembers,
              handleCheckboxChange,
              handleBack: handleCancel,
              handleNext: handleAddMembers,
              title: USER_GROUP_DETAILS_CONSTANTS.ADD_MEMBERS,
              searchQuery,
              handleSearchChange,
              newSelectedMembers,
              setNewSelectedMembers,
              page: USER_GROUP_DETAILS_CONSTANTS.PAGE,
              loading,
            })}
      </Modal>
    </div>
  );
}

export default UserGroupsDetails;
